import axios from "axios";
import config from "../../config.json";
import { General } from "../constants/common";
import { isEmpty, getEnvironmentVariables, getRandomId, isFull } from "../utils";

let axiosInterceptor = null; // outer variable

export const getApi = ({ baseUrl, responseConfig, onTokenRefresh, onSignInExpired }) => {
  const instance = axios.create({
    ...{
      baseURL: baseUrl,
    },
  });

  if (!!axiosInterceptor || axiosInterceptor === 0) {
    instance.interceptors.request.eject(axiosInterceptor);
  }

  // Add a request interceptor to handle headers
  axiosInterceptor = instance.interceptors.request.use(
    (config) => {
      const token = config.meta?.accessToken;

      //config.headers = headers;
      if (isFull(token)) {
        config.headers = {
          ...(config.headers || {}),
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json;charset=UTF-8",
        };
      }
      else {
        config.headers = {
          ...(config.headers || {}),
          "Content-Type": "application/json;charset=UTF-8",
        };
      }

      // Every call carries a correlation id, so its log lines can be found together in every
      // service it reaches. A call that is sent again keeps the id it already has.
      if (isEmpty(config.headers[General.Headers.CorrelationId])) {
        config.headers[General.Headers.CorrelationId] = getRandomId();
      }

      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  //Add a response interceptor to handle refresh token behavior
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async function (error) {
      const originalRequest = error.config;

      if (isEmpty(error.response)) {
        return Promise.reject(error);
      }

      const isRefused =
        error.response.status === 401 || error.response.status === 403;

      // The refresh itself was refused, so the sign-in is over. Signing out here
      // stops the screens that poll from calling again with dead tokens.
      if (isRefused && originalRequest.url.includes(config.accounts.refreshTokenUrl)) {
        onSignInExpired?.();
        return Promise.reject(error);
      }

      if (
        isRefused &&
        (originalRequest.url.includes(config.loginUrl) ||
          error.response?.data?.code === "InvalidCredentials" ||
          error.response?.data?.code === "UserDisabled" ||
          error.response?.data?.code === "LoggedInButUserNotFound")
      ) {
        return Promise.reject(error);
      }

      const refreshToken = originalRequest.meta?.refreshToken;

      // Only a signed-in request can be refreshed. A visitor's 401 is passed on as it is.
      if (
        (error.response.status === 401) &&
        !originalRequest._retry &&
        isFull(refreshToken)
      ) {
        originalRequest._retry = true;

        const accessToken = originalRequest.meta?.accessToken;

        return await instance
          .request({
            baseURL: getEnvironmentVariables().REACT_APP_IDENTITY_API_URL,
            url: config.accounts.refreshTokenUrl,
            method: "POST",
            data: {
              accessToken,
              refreshToken,
            },
            headers: {
              "Content-Type": "application/json;charset=UTF-8"
            },
          })
          .then((response) => {
            if (response.status === 200) {
              //dispatch to update state with new tokens => this could move out to the middleware - optional
              // dispatch(doRefreshToken(response.data.user));

              const newAccessToken = response.data.user.accessToken;
              const newRefreshToken = response.data.user.refreshToken;

              onTokenRefresh?.({
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
              });

              originalRequest.meta = {
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
              };

              originalRequest.headers[
                "Authorization"
              ] = `Bearer ${newAccessToken}`;

              return instance(originalRequest);
            }
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      }
      return Promise.reject(error);
    }
  );

  return instance;
};
